const STORAGE_KEY = 'refy-notifications'
const MAX_STORED_IDS = 100

class NotificationsDrawer extends window.BAO.CustomElement() {
  connectedCallback () {
    if (this.hasBeenInitialised) return

    super.connectedCallback()

    this.state = this.getState()
    this.drawerWasOpen = false

    this.render()
    this.observeDrawer()
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(this, 'click', this.handleClick.bind(this))
    this.listeners.add(this, 'shopify:block:select', () => window.drawers?.open('notifications'))
    this.listeners.add(document, 'shopify:section:select', this.handleSectionSelect.bind(this))
    this.listeners.add(document, 'shopify:section:deselect', this.handleSectionDeselect.bind(this))
    this.listeners.add(document, 'shopify:section:unload', this.handleSectionUnload.bind(this))
  }

  observeDrawer () {
    if (!this.drawer) return

    this.observer = new MutationObserver(() => {
      const isOpen = this.drawer.classList.contains('drw-Drawer-active')
      if (isOpen === this.drawerWasOpen) return

      this.drawerWasOpen = isOpen

      if (isOpen) {
        this.handleOpen()
      } else {
        this.handleClose()
      }
    })

    this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['class'] })
  }

  render () {
    const now = Date.now()

    this.notifications.forEach((notificationEl) => {
      const id = notificationEl.dataset.notificationId
      const isDismissed = !this.isDesignMode && this.state.dismissed.includes(id)
      const isVisible = this.isScheduled(notificationEl, now) && !isDismissed

      notificationEl.hidden = !isVisible
      notificationEl.toggleAttribute('data-unread', isVisible && !this.state.seen.includes(id))
    })

    this.update()
  }

  update () {
    const visibleCount = this.visibleNotifications.length

    const { count, empty } = this.els

    if (count?.exists) {
      count.element.textContent = visibleCount > 0 ? `(${visibleCount})` : ''
    }

    if (empty?.exists) {
      empty.element.hidden = visibleCount > 0
    }

    const hasUnread = this.visibleNotifications.some(
      (notificationEl) => !this.state.seen.includes(notificationEl.dataset.notificationId),
    )

    this.externalEls.dot?.elements.forEach((dotEl) => {
      dotEl.hidden = !hasUnread
    })
  }

  handleOpen () {
    this.visibleNotifications.forEach((notificationEl) => {
      const id = notificationEl.dataset.notificationId

      this.addToState('seen', id)

      if (this.shouldHideAfterSeen(notificationEl) && !notificationEl.querySelector('a')) {
        this.addToState('dismissed', id)
      }
    })

    this.saveState()
    this.update()
  }

  handleClose () {
    // Unread markers stay visible while the drawer is open, then clear for future viewings
    this.notifications.forEach((notificationEl) => notificationEl.removeAttribute('data-unread'))

    if (!this.isDesignMode) {
      this.notifications
        .filter((notificationEl) => this.state.dismissed.includes(notificationEl.dataset.notificationId))
        .forEach((notificationEl) => {
          notificationEl.hidden = true
        })
    }

    this.update()
  }

  handleClick (event) {
    const notificationEl = event.target.closest('[data-notifications-drawer-el="notification"]')
    if (!notificationEl || !event.target.closest('a')) return
    if (!this.shouldHideAfterSeen(notificationEl)) return

    this.addToState('dismissed', notificationEl.dataset.notificationId)
    this.saveState()
  }

  handleSectionSelect (event) {
    if (event.detail?.sectionId !== this.dataset.sectionId) return

    window.drawers?.open('notifications')
  }

  handleSectionDeselect (event) {
    if (event.detail?.sectionId !== this.dataset.sectionId) return

    window.drawers?.closeDrawer()
  }

  handleSectionUnload (event) {
    if (event.detail?.sectionId !== this.dataset.sectionId) return

    this.observer?.disconnect()
    this.listeners.removeAll()

    if (window.drawers) {
      window.drawers.drawers = window.drawers.drawers.filter((drawer) => drawer !== this.drawer)
    }

    this.drawer?.remove()
  }

  /**
   * @param {HTMLElement} notificationEl
   * @param {number} now
   *
   * @returns {boolean}
   */
  isScheduled (notificationEl, now) {
    // Re-checked client side as the page HTML may be cached past a start or end date
    const start = Number(notificationEl.dataset.start) * 1000
    const end = Number(notificationEl.dataset.end) * 1000

    if (start > 0 && start > now) return false
    if (end > 0 && end <= now) return false

    return true
  }

  /**
   * @param {HTMLElement} notificationEl
   *
   * @returns {boolean}
   */
  shouldHideAfterSeen (notificationEl) {
    return notificationEl.dataset.hideAfterSeen === 'true'
  }

  /**
   * @param {'seen'|'dismissed'} list
   * @param {string} id
   */
  addToState (list, id) {
    if (this.state[list].includes(id)) return

    this.state[list] = [...this.state[list], id].slice(-MAX_STORED_IDS)
  }

  /**
   * @returns {{ seen: string[], dismissed: string[] }}
   */
  getState () {
    try {
      const state = JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {}

      return {
        seen: Array.isArray(state.seen) ? state.seen : [],
        dismissed: Array.isArray(state.dismissed) ? state.dismissed : [],
      }
    } catch (e) {
      return { seen: [], dismissed: [] }
    }
  }

  saveState () {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state))
    } catch (e) {}
  }

  /**
   * @returns {HTMLElement[]}
   */
  get notifications () {
    return this.els.notification?.elements ?? []
  }

  /**
   * @returns {HTMLElement[]}
   */
  get visibleNotifications () {
    return this.notifications.filter((notificationEl) => !notificationEl.hidden)
  }

  /**
   * @returns {HTMLElement|null}
   */
  get drawer () {
    return this.closest('site-drawer')
  }

  /**
   * @returns {boolean}
   */
  get isDesignMode () {
    return Boolean(window.Shopify?.designMode)
  }
}

if (!customElements.get('notifications-drawer')) {
  customElements.define('notifications-drawer', NotificationsDrawer)
}
